import React, { useRef, useState } from 'react';

import { cssMap, cx } from '@compiled/react';
import { useDebouncedCallback } from 'use-debounce';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { IconButton } from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type {
	CarouselElement,
	ParsedElement,
	PersonElement,
	ProjectHighlightElement,
	// PersonElement,
	SmartLinkElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import type { ComponentPosition } from './dynamic-layout-component';
import { DynamicLayoutPersonCard } from './dynamic-layout-person-card';
import { DynamicLayoutSmartLink } from './dynamic-layout-smart-link';
import { DynamicLayoutProjectHighlightCard } from './sub-agents/dynamic-layout-project-highlight-card';

// TODO: Fix wonky scroll behaviour on carousel
const carouselCardWidth = 355; // card width (300px) + gap (space.200 = 16px)
const scrollWidth = (carouselCardWidth + 46) * 2;

const styles = cssMap({
	carouselStyles: {
		display: 'flex',
		alignItems: 'flex-start',
		position: 'relative',
		flexDirection: 'column',
		width: '100%',
		gap: token('space.050'),
	},
	carouselStylesSingleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		position: 'relative',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: token('space.negative.200'),
	},
	scrollContainerStyles: {
		overflowX: 'auto',
		overflowY: 'hidden',
		display: 'inline-block',
		maxWidth: '100%',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	cardContainerStyles: {
		display: 'flex',
		gap: token('space.200'),
		whiteSpace: 'nowrap',
	},
});

export type DocumentsCarouselProps = {
	component: CarouselElement;
	position: ComponentPosition;
	showArrows?: boolean;
};

const isScrollAtLeft = (elem: HTMLDivElement | null) => elem?.scrollLeft === 0;
const isScrollAtRight = (elem: HTMLDivElement | null) =>
	elem ? Math.abs(elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth) <= 1 : false;

export const DynamicLayoutCarousel = ({
	component,
	position,
	showArrows = true,
}: DocumentsCarouselProps): React.JSX.Element | null => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [hideLeftScroll, setHideLeftScroll] = useState(false);
	const [hideRightScroll, setHideRightScroll] = useState(false);

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const key_id = useRef(uuidv4());

	const shouldAlignHeader = position === 'single-column';

	// useEffect(() => {
	// 	if (scrollContainerRef.current) {
	// 		setHideLeftScroll(isScrollAtLeft(scrollContainerRef.current));
	// 		setHideRightScroll(isScrollAtRight(scrollContainerRef.current));
	// 	}
	// }, [scrollContainerRef.current?.scrollWidth]);

	const onScroll = useDebouncedCallback(() => {
		setHideLeftScroll(isScrollAtLeft(scrollContainerRef.current));
		setHideRightScroll(isScrollAtRight(scrollContainerRef.current));
	}, 30);

	const carouselChildren = (component.children || []) as ParsedElement[];

	const scrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollLeft -= scrollWidth;
		}
	};

	const scrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollLeft += scrollWidth;
		}
	};

	if (!carouselChildren || carouselChildren.length === 0) {
		return null;
	}

	return (
		<Box
			xcss={cx(shouldAlignHeader ? styles.carouselStylesSingleRow : styles.carouselStyles)}
			testId="dynamic-layout-carousel"
		>
			<Inline spread="space-between" grow="fill" alignBlock="center">
				{!shouldAlignHeader && component.attributes?.title ? (
					<Inline space="space.100" alignBlock="center">
						<Text
							weight="semibold"
							size="small"
							color="color.text.accent.gray"
							testId="carousel-title"
						>
							{component.attributes?.title}
						</Text>
					</Inline>
				) : (
					<Box />
				)}
				{showArrows && (
					<Inline space="space.100">
						{!hideLeftScroll && (
							<IconButton
								icon={ArrowLeftIcon}
								label="scroll left"
								onClick={scrollLeft}
								shape="circle"
								appearance="subtle"
								spacing="compact"
							/>
						)}
						{!hideRightScroll && (
							<IconButton
								icon={ArrowRightIcon}
								label="scroll right"
								onClick={scrollRight}
								shape="circle"
								appearance="subtle"
								spacing="compact"
							/>
						)}
					</Inline>
				)}
			</Inline>
			<Box xcss={styles.scrollContainerStyles} ref={scrollContainerRef} onScroll={onScroll as any}>
				<Box xcss={styles.cardContainerStyles}>
					{carouselChildren.map((child, index) => {
						//TODO : move to a separate component
						if (typeof child !== 'string' && child.tag === 'people-card') {
							return (
								<Box key={`${child.tag}-${key_id}-${index}`} testId="carousel-item-people-card">
									<DynamicLayoutPersonCard component={child as PersonElement} />
								</Box>
							);
						}
						if (typeof child !== 'string' && child.tag === 'smart-link') {
							return (
								<Box key={`${child.tag}-${key_id}-${index}`} testId="carousel-item-smart-link">
									<DynamicLayoutSmartLink component={child as SmartLinkElement} />
								</Box>
							);
						}
						if (typeof child !== 'string' && child.tag === 'project-highlight-card') {
							return (
								<Box
									key={`${child.tag}-${key_id}-${index}`}
									testId="carousel-item-project-highlight-card"
								>
									<DynamicLayoutProjectHighlightCard component={child as ProjectHighlightElement} />
								</Box>
							);
						}
						return <Box key={`${key_id}-${index}`} />;
					})}
				</Box>
			</Box>
		</Box>
	);
};
